// @flow

import * as React from 'react';
import { localize } from '@ta/common.i18n';
import { bind } from '@ta/common.api';
import classnames from '@ta/common.classnames';
import { Target } from '@ta/overlays.pieces';
import { Icon } from '@ta/styleguide.icon';
import { ClickTracker } from '@ta/common.tracking';
import ShoppingCartRecapture from './ShoppingCartRecapture';

import styles from './ShoppingCart.less';

type Props = {
  /** The number of items in the shopping cart. */
  count?: number,

  /** True if we should show the shopping cart. */
  show?: boolean,

  /** The servlet name, used for tracking */
  servletName?: string | null,

  /**
   * Render prop tells the parent if we're showing the shopping cart.
   * Allows us to conditionally render a separator in the nav near the shopping cart icon.
   */
  children?: React.Node => React.Node,

  /** Whether we're on desktop. Used for shopping cart recapture */
  isDesktop: boolean,
};

type State = {
  animated: boolean,
};

/**
 * The shopping cart action link in the global nav
 */
export class ShoppingCart extends React.Component<Props, State> {
  static defaultProps = {
    count: 0,
    show: true,
  };

  state = {
    animated: false,
  };

  animate = () => {
    this.setState({
      animated: true,
    });
  };

  render() {
    if (!this.props.show) {
      return this.props.children ? this.props.children(null) : null;
    }

    // if we have >0 items in the cart then show the jewel
    const jewel = this.props.count ? (
      <span className={`shopping-cart-size ui_jewel ${styles.jewel}`}>{this.props.count}</span>
    ) : null;

    const clickTrackingProps = {
      module: `TopNav_${this.props.servletName || ''}`,
      action: 'click',
      context: 'Shopping_Cart',
    };

    return (
      <Target>
        {(setTarget, target) => {
          const recapture =
            this.props.count && this.props.isDesktop && target ? (
              // $FlowFixMe: It can't handle the extra props from bind here? Idk why.
              <ShoppingCartRecapture below={target} onShow={this.animate} />
            ) : null;
          const contents = (
            <ClickTracker {...clickTrackingProps}>
              <a
                ref={setTarget}
                href="/ShoppingCart"
                className={classnames(styles.shoppingCartLink, {
                  [styles.animated]: this.state.animated,
                })}
                title={localize('utility_nav_cart')}
              >
                <Icon name="empty-cart" className={styles.icon} />
                <span className={styles.label}>
                  {localize('attractions.cart_button_acc')} {/* "Cart" */}
                </span>
              </a>
              {jewel}
              {recapture}
            </ClickTracker>
          );

          return this.props.children ? this.props.children(contents) : contents;
        }}
      </Target>
    );
  }
}

/**
 * The shopping cart action link container in the global nav. It stages data for the number
 * of items in the cart and provides it to <ShoppingCart />
 */
export default bind<Props, {||}, _, _, _, _>((state, stage) => {
  const { meta } = state;
  const servletName = (meta && meta.initialServletName) || '';
  const staged = stage(`/data/1.0/cart/info?servletName=${servletName}`);

  ShoppingCartRecapture.stage(state, stage);

  return () => {
    const { data } = staged.get();
    const { count = 0, show = false } = data || { count: 0, show: false };
    return {
      count,
      show,
      servletName: state.meta.initialServletName,
      isDesktop: state.meta.device.viewportCategory === 'DESKTOP',
    };
  };
})(ShoppingCart);
